/**
 * Map highlight for elevation-panel-v2's drag-to-select-a-range feature —
 * draws the selected sub-range of the route as a bright overlaid line on the
 * Mapbox map, so a selection made on the chart is visible on the hike itself
 * too, not just as chart shading.
 *
 * Mirrors elevationMarker.ts's lifecycle shape (isolated per-instance
 * controller, lazy `getMap` accessor tolerant of the map not existing yet),
 * but layers a real GL line via `window.layerRegistry` — the established
 * pattern for annotation layers in this codebase (see
 * plan_trail/store/modules/actions/highlights.ts, base_map_dialog's
 * LineSourceHelper) — rather than a DOM marker, since a multi-point polyline
 * isn't something mapboxgl.Marker can express.
 *
 * `window.layerRegistry` is set up by both hosts that pass `getMap` into
 * ElevationPanelV2.vue (HikeMap.vue for planned routes, RecordingMap.vue for
 * recordings) before their map-dependent UI mounts, so it's expected to
 * exist whenever `getMap()` resolves to a real map; `show`/`hide` no-op
 * defensively if it doesn't.
 */
import mapboxgl from 'mapbox-gl';
import { MapLayerCategory } from '../layer-registry';

const SOURCE_ID = 'epv2-range-highlight';
const BORDER_LAYER_ID = 'epv2-range-highlight-border';
const LINE_LAYER_ID = 'epv2-range-highlight-line';

// Brand gold, matching the elevation chart's own line colour, so the
// highlighted segment reads as "the same selection" across chart and map
// regardless of the route's own colour (planned routes/alts/spurs are drawn
// in several different colours — red/cyan/magenta — so the highlight can't
// just be "brighter than the route").
const HIGHLIGHT_COLOR = '#FFDF46';
const HIGHLIGHT_BORDER_COLOR = 'rgba(0, 0, 0, 0.55)';

// Above both hike-lines (z: 10) and recording-line (z: 9) so the highlight
// always draws on top of the base route line in either mode.
const BORDER_Z = 11;
const LINE_Z = 12;

export interface RangeHighlightController {
  /** Show (or move/resize) the highlight along the given `[lng, lat]` coordinates. */
  show(coordinates: number[][]): void;
  /** Hide the highlight without tearing down its layers (cheap to re-show). */
  hide(): void;
  /** Permanently remove the highlight's layers/source; call on unmount. */
  destroy(): void;
}

function emptyLineFeature(): GeoJSON.Feature<GeoJSON.LineString> {
  return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } };
}

function lineFeature(coordinates: number[][]): GeoJSON.Feature<GeoJSON.LineString> {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: coordinates.map(([lon, lat]) => [lon, lat]) },
  };
}

export function createRangeHighlight(getMap: () => mapboxgl.Map | null | undefined): RangeHighlightController {
  let created = false;

  function ensureLayers(map: mapboxgl.Map): boolean {
    if (created) return true;
    const registry = window.layerRegistry;
    if (!registry) return false;

    registry.addLayer(
      {
        id: BORDER_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': HIGHLIGHT_BORDER_COLOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 6, 14, 12],
        },
      },
      MapLayerCategory.Annotations,
      { z: BORDER_Z, source: { id: SOURCE_ID, spec: { type: 'geojson', data: emptyLineFeature() } } }
    );
    registry.addLayer(
      {
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': HIGHLIGHT_COLOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 3, 14, 7],
        },
      },
      MapLayerCategory.Annotations,
      { z: LINE_Z }
    );

    created = true;
    return true;
  }

  const show = (coordinates: number[][]): void => {
    const map = getMap();
    if (!map || typeof map.getCenter !== 'function') return;
    if (!ensureLayers(map)) return;

    try {
      const registry = window.layerRegistry;
      registry?.updateSource(SOURCE_ID, (source) => {
        (source as mapboxgl.GeoJSONSource).setData(lineFeature(coordinates));
      });
      map.setLayoutProperty(BORDER_LAYER_ID, 'visibility', 'visible');
      map.setLayoutProperty(LINE_LAYER_ID, 'visibility', 'visible');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating range highlight on map:', error);
    }
  };

  const hide = (): void => {
    const map = getMap();
    if (!map || !created) return;
    try {
      map.setLayoutProperty(BORDER_LAYER_ID, 'visibility', 'none');
      map.setLayoutProperty(LINE_LAYER_ID, 'visibility', 'none');
    } catch {
      // Style may already be gone (e.g. map torn down) — nothing to clean up.
    }
  };

  const destroy = (): void => {
    const map = getMap();
    if (!map || !created) return;
    const registry = window.layerRegistry;
    try {
      if (registry) {
        registry.removeLayer(BORDER_LAYER_ID);
        registry.removeLayer(LINE_LAYER_ID);
      } else {
        if (map.getLayer(BORDER_LAYER_ID)) map.removeLayer(BORDER_LAYER_ID);
        if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      }
    } catch {
      // Style may already be gone (e.g. map torn down) — nothing to clean up.
    }
    created = false;
  };

  return { show, hide, destroy };
}
